<template>
    <div class="static-page">
        <div :class="['static-page__layout', { 'static-page__layout--full': !computedToc.length }]">

            <!-- Sticky TOC rail -->
            <nav class="static-page__toc" v-if="computedToc.length">
                <!-- Mobile: dropdown toggle -->
                <button
                    class="static-page__toc-toggle"
                    @click="mobileOpen = !mobileOpen"
                    :aria-expanded="mobileOpen.toString()"
                >
                    <span class="static-page__toc-toggle-label">On this page</span>
                    <span class="static-page__toc-toggle-active" v-if="activeTocItem">{{ activeTocItem.text }}</span>
                    <b-icon-chevron-down class="static-page__toc-toggle-icon" :class="{ 'is-open': mobileOpen }"/>
                </button>

                <!-- Link list — always visible on desktop, toggle on mobile -->
                <div :class="['static-page__toc-list', { 'is-open': mobileOpen }]">
                    <div class="static-page__toc-label">On this page</div>
                    <a
                        v-for="(item, index) in computedToc"
                        :key="item.id"
                        href="#"
                        :class="['static-page__toc-item', { 'static-page__toc-item--active': activeId === item.id }]"
                        @click.prevent="scrollTo(item.id); mobileOpen = false"
                    >
                        <span class="static-page__toc-badge">{{ pad(index + 1) }}</span>
                        {{ item.text }}
                    </a>
                </div>
            </nav>

            <!-- Content card -->
            <main class="static-page__card" ref="content">
                <div class="static-page__card-inner">
                    <slot/>
                </div>
            </main>

        </div>
    </div>
</template>

<script>
export default {
    name: 'StaticPage',
    props: {
        tocItems: { type: Array, default: null }
    },
    data() {
        return { autoToc: [], activeId: null, observer: null, mobileOpen: false }
    },
    computed: {
        computedToc() { return this.tocItems || this.autoToc },
        activeTocItem() {
            return this.computedToc.find(item => item.id === this.activeId) || null
        }
    },
    mounted() {
        this.$nextTick(() => {
            if (!this.tocItems) this.buildToc()
            else {
                if (this.tocItems.length) this.activeId = this.tocItems[0].id
                this.setupObserver()
            }
        })
    },
    beforeDestroy() {
        if (this.observer) this.observer.disconnect()
    },
    methods: {
        pad(n) { return String(n).padStart(2, '0') },

        buildToc() {
            const inner = this.$refs.content && this.$refs.content.querySelector('.static-page__card-inner')
            if (!inner) return
            const headings = Array.from(inner.querySelectorAll('h2'))
            if (!headings.length) return

            this.autoToc = headings.map((h2, i) => {
                const text = h2.textContent.trim()
                const sectionId = `section-${i}`

                // Create section wrapper
                const wrapper = document.createElement('div')
                wrapper.className = 'static-page__section'
                wrapper.id = sectionId

                // Divider before each section
                const divider = document.createElement('div')
                divider.className = 'static-page__divider'

                // Badge
                const badge = document.createElement('div')
                badge.className = 'static-page__section-badge'
                badge.textContent = this.pad(i + 1)

                // Body — collect h2 and all following siblings until next h2
                const body = document.createElement('div')
                body.className = 'static-page__section-body'
                const nodes = [h2]
                let next = h2.nextElementSibling
                while (next && next.tagName !== 'H2') {
                    nodes.push(next)
                    next = next.nextElementSibling
                }

                // Insert wrapper before h2 in the DOM.
                // Only add a divider for sections after the first — it ends up
                // between the previous wrapper and this one, never trailing.
                if (i > 0) {
                    h2.parentNode.insertBefore(divider, h2)
                    h2.parentNode.insertBefore(wrapper, divider)
                } else {
                    h2.parentNode.insertBefore(wrapper, h2)
                }

                // Move nodes into body (automatically removes from original parent)
                nodes.forEach(n => body.appendChild(n))
                wrapper.appendChild(badge)
                wrapper.appendChild(body)

                return { id: sectionId, text, level: 'H2' }
            })

            if (this.autoToc.length) this.activeId = this.autoToc[0].id
            this.setupObserver()
        },

        setupObserver() {
            const el = this.$refs.content
            if (!el || typeof IntersectionObserver === 'undefined') return
            const targets = el.querySelectorAll('.static-page__section, button[id]')
            if (!targets.length) return
            if (this.observer) this.observer.disconnect()
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(e => { if (e.isIntersecting) this.activeId = e.target.id })
            }, { rootMargin: '-10% 0px -70% 0px', threshold: 0 })
            targets.forEach(t => this.observer.observe(t))
        },

        scrollTo(id) {
            this.activeId = id
            const el = document.getElementById(id)
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }
}
</script>

<style lang="scss">
@import "/sass/variables_mixins";

// Approximate oklch values from the design as hex
$sp-green-dark:   #2d6a4f;   // oklch(0.42 0.1 150) — active nav, links, badge text
$sp-green-light:  #edf5ef;   // oklch(0.95 0.025 150) — badge bg, inactive
$sp-border:       #e6ece7;   // oklch(0.92 0.01 130) — card border, dividers
$sp-text-dark:    #253b2c;   // oklch(0.27 0.03 150) — h2, h1
$sp-text-body:    #4a5c50;   // oklch(0.38 0.01 150) — body paragraphs
$sp-text-muted:   #7a8d7e;   // oklch(0.55 0.02 150) — TOC label, meta
$sp-gradient-top: #f1f7f2;   // oklch(0.97 0.015 140) — header gradient start

// 8px grid token reference (rem):
// 0.25 = 4px | 0.5 = 8px | 1 = 16px | 1.5 = 24px | 2 = 32px
// 2.5 = 40px | 3 = 48px  | 3.5 = 56px | 4 = 64px
// Horizontal padding throughout: 1.5rem (24px)

.static-page {
    max-width: 1200px !important;
    margin: 0 auto !important;
    padding: 2.5rem 0 4rem !important;  // no side padding on mobile

    @media (min-width: 820px) {
        padding: 2.5rem 1.5rem 4rem !important;
    }

    &__layout {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1.5rem;   // 24px
        align-items: start;

        @media (min-width: 820px) {
            grid-template-columns: 260px minmax(0, 1fr);
            gap: 2.5rem;  // 40px
        }

        &--full {
            grid-template-columns: minmax(0, 600px) !important;
            justify-content: center;
        }
    }

    // ── TOC sidebar ────────────────────────────────────────────────
    &__toc {
        order: 1;

        @media (min-width: 820px) {
            order: 1;
            position: sticky;
            top: 2rem;
            padding-right: 0.5rem;
        }
    }

    // Mobile dropdown toggle — hidden on desktop
    &__toc-toggle {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 100%;
        padding: 0.75rem 1rem;
        background: #fff;
        border: 1px solid $sp-border;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
        color: $sp-text-dark;
        text-align: left;

        @media (min-width: 820px) {
            display: none;
        }
    }

    &__toc-toggle-label {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: $sp-text-muted;
        flex-shrink: 0;
    }

    &__toc-toggle-active {
        flex: 1;
        font-weight: 600;
        color: $sp-text-dark;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__toc-toggle-icon {
        flex-shrink: 0;
        color: $sp-text-muted;
        transition: transform 0.2s;

        &.is-open { transform: rotate(180deg); }
    }

    // Link list — collapsed on mobile, always visible on desktop
    &__toc-list {
        display: none;
        flex-direction: column;
        gap: 0.25rem;
        margin-top: 0.5rem;

        &.is-open { display: flex; }

        @media (min-width: 820px) {
            display: flex;
            margin-top: 0;
        }
    }

    &__toc-label {
        //font-size: 12px;
        //font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: $sp-text-muted;
        margin-bottom: 0.5rem;
        padding-left: 1rem;

        // Label is redundant inside the dropdown toggle — hide on mobile
        @media (max-width: 819px) {
            display: none;
        }
    }

    &__toc-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;          // 8px
        padding: 0.5rem 1rem; // 8px 16px
        border-radius: 8px;
        text-decoration: none;
        //font-size: 13.5px;
        line-height: 1.3;
        color: $sp-text-body;
        font-weight: 500;
        transition: background 0.15s, color 0.15s;

        &:hover {
            background: #c4e7cd;
            color: $sp-green-dark;
            text-decoration: none;
        }

        &--active {
            background: $sp-green-dark;
            color: #fff;
            font-weight: 600;
            text-decoration: none;

            .static-page__toc-badge {
                background: rgba(0, 0, 0, 0.2);
                color: #fff;
            }

            &:hover {
                background: #c4e7cd; 
                color: #fff;
                text-decoration: none;
            }
        }
    }

    &__toc-badge {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        border-radius: 6px;
        background: $sp-green-light;
        color: $sp-green-dark;
        font-size: 14px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s, color 0.15s;
    }

    // ── Content card ───────────────────────────────────────────────
    &__card {
        order: 2;
        background: #fff;
        border-radius: 16px;
        border: 1px solid $sp-border;
        box-shadow: 0 1px 2px rgba(20, 40, 25, 0.06), 0 12px 32px rgba(20, 40, 25, 0.08);
        overflow: hidden;

        @media (min-width: 820px) {
            order: 2;
        }
    }

    &__card-inner {
        // H1 — gradient header: 2.5rem top, 1.5rem sides, 2rem bottom
        > h1:first-child {
            // Scales from 1.5rem (24px) on small phones up to 2.25rem (36px) on large screens
            font-size: clamp(1.5rem, calc(1rem + 2vw), 2.25rem);
            font-weight: 800;
            color: $sp-text-dark;
            letter-spacing: -0.02em;
            margin: 0;
            padding: 2.5rem 1.5rem 2rem;
            background: linear-gradient(180deg, $sp-gradient-top, #fff);
            border-bottom: 1px solid $sp-border;
        }

        // Everything except h1 and the auto-generated section/divider wrappers
        // gets side padding — catches intro paragraphs AND tutorial accordion divs
        > *:not(h1):not(.static-page__section):not(.static-page__divider) {
            padding-left: 1.5rem;   // 24px
            padding-right: 1.5rem;
        }

        // Intro paragraphs before the first h2
        > p, > ul, > ol, > span {
            font-size: 15.5px;
            line-height: 1.75;
            color: $sp-text-body;
            margin-bottom: 1rem; // 16px

            &:first-of-type { padding-top: 2rem; } // 32px
        }

        > *:last-child:not(.static-page__divider) { padding-bottom: 3rem; } // 48px
    }

    // ── Section wrappers (auto-built by buildToc) ──────────────────
    &__divider {
        height: 1px;
        background: $sp-border;
        margin: 0 1.5rem;  // 24px — matches section side padding
    }

    &__section {
        display: flex;
        gap: 1rem;                  // 16px between badge and body
        padding: 2rem 1.5rem 0.5rem; // 32px top, 24px sides, 8px bottom
    }

    &__section-badge {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background: $sp-green-light;
        color: $sp-green-dark;
        font-size: 13px;
        font-weight: 800;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 0.25rem;  // 4px — aligns with first line of h2
    }

    &__section-body {
        flex: 1;
        min-width: 0;
        padding-bottom: 1.5rem;  // 24px

        h2 {
            font-size: 21px;
            font-weight: 700;
            color: $sp-text-dark;
            letter-spacing: -0.01em;
            margin: 0 0 1rem;  // 16px
        }

        h3 {
            font-size: 14px;
            font-weight: 700;
            color: $sp-text-dark;
            margin: 1.5rem 0 0.5rem;  // 24px top, 8px bottom
        }

        p {
            font-size: 15.5px;
            line-height: 1.75;
            color: $sp-text-body;
            margin: 0 0 1rem;  // 16px
        }

        ul, ol {
            margin: 0 0 1rem 1.5rem;  // 16px bottom, 24px left

            li {
                font-size: 15.5px;
                line-height: 1.75;
                color: $sp-text-body;
                margin-bottom: 0.25rem;  // 4px
            }
        }

        a {
            color: $sp-green-dark;
            font-weight: 600;
        }

        span {
            font-size: 15.5px;
            color: $sp-text-body;
        }
    }

    // ── Tutorial accordion (inside card, no section wrapping) ───────
    .tutorial__toggle {
        color: $sp-text-dark;
        font-weight: 600;
    }
}
</style>
